import React, { useState } from 'react';
import { Shield, FileText, Check } from 'lucide-react';
import Button from './Button';
import { PrivacyPolicy, TermsOfService } from '../features/legal';

type PolicyTab = 'privacy' | 'terms';

interface PolicyAcceptanceModalProps {
  onAccept: () => Promise<void>;
  isLoading?: boolean;
}

const PolicyAcceptanceModal: React.FC<PolicyAcceptanceModalProps> = ({ onAccept, isLoading = false }) => {
  const [activeTab, setActiveTab] = useState<PolicyTab>('privacy');
  const [hasReadPrivacy, setHasReadPrivacy] = useState(true);
  const [hasReadTerms, setHasReadTerms] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const tabs = [
    { id: 'privacy' as PolicyTab, label: 'Privacy Policy', icon: Shield, read: hasReadPrivacy },
    { id: 'terms' as PolicyTab, label: 'Terms of Service', icon: FileText, read: hasReadTerms },
  ];

  const handleTabChange = (tab: PolicyTab) => {
    setActiveTab(tab);
    if (tab === 'privacy') setHasReadPrivacy(true);
    if (tab === 'terms') setHasReadTerms(true);
  };

  const allRead = hasReadPrivacy && hasReadTerms;
  const canAccept = allRead && accepted;

  const handleAccept = async () => {
    if (canAccept) {
      await onAccept();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/70 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-[#E8FCFA] to-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#07E5D1]/20 flex items-center justify-center">
              <Shield className="w-5 h-5 text-[#07E5D1]" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Review Our Policies</h2>
              <p className="text-sm text-gray-500">Please review and accept to continue</p>
            </div>
          </div>
        </div>

        <div className="flex bg-gray-50 border-b border-gray-100">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`flex-1 px-4 py-3 flex items-center justify-center gap-2.5 text-sm font-medium transition-all relative ${
                activeTab === tab.id
                  ? 'text-[#07E5D1] bg-white'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              {activeTab === tab.id && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#07E5D1]" />
              )}
              <tab.icon className="w-4 h-4" />
              <span>{tab.label}</span>
              {tab.read && (
                <span className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Check className="w-3 h-3 text-emerald-600" />
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 min-h-[320px] bg-white">
          {activeTab === 'privacy' && <PrivacyPolicy embedded />}
          {activeTab === 'terms' && <TermsOfService embedded />}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 space-y-4">
          {!allRead && (
            <p className="text-sm text-gray-500 text-center">
              Please review both policies by clicking each tab
            </p>
          )}

          <label className="flex items-center gap-3 cursor-pointer justify-center">
            <div className="relative">
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                disabled={!allRead}
                className="sr-only peer"
              />
              <div className={`w-5 h-5 rounded border-2 transition-all flex items-center justify-center ${
                !allRead 
                  ? 'border-gray-200 bg-gray-50' 
                  : accepted 
                    ? 'border-[#07E5D1] bg-[#07E5D1]' 
                    : 'border-gray-300 bg-white hover:border-[#07E5D1]'
              }`}>
                {accepted && <Check className="w-3.5 h-3.5 text-white" />}
              </div>
            </div>
            <span className={`text-sm ${allRead ? 'text-gray-700' : 'text-gray-400'}`}>
              I have read and agree to the Privacy Policy and Terms of Service
            </span>
          </label>

          <Button
            onClick={handleAccept}
            disabled={!canAccept || isLoading}
            fullWidth
          >
            {isLoading ? 'Processing...' : 'Accept & Continue'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PolicyAcceptanceModal;
